import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ResultStatusType } from 'antd/lib/result';
import debug from 'debug';
import { Icons } from 'icons';

import ItemTypeIcon from 'components/common/item-type-icon';
import StepLayout, { STEP_INDEX } from 'components/step-layout';
import { StepType } from 'components/step-layout/type';
import { updateItem } from 'lib/apis/item';
import { classnames } from 'lib/classnames';
import fetch from 'lib/fetch';
import { useI18n, useToken } from 'lib/hooks';
import useGlobalConfig from 'lib/hooks/useGlobalConfig';
import { ItemType, Status, Workspace } from 'lib/models';
import { Item, ObjectId } from 'lib/types/models';
import { ResultType, WorkflowDataProps } from 'lib/types/workflow';

import ChangeOperator, { DEFAULT_FLOW_MESSAGE, SelectTransitionType } from './changeOperator';
import ChangeView from './changeView';
import FinishView from './finishView';
import SelectWorkspaceAndItemType from './selectWorkspaceAndItemType';

import styles from './index.module.less';

const cx = classnames(styles);
const log = debug('itemMove:page');

interface ItemsMoveProps {
  workspaceKey: string;
  workspaceId: ObjectId;
  item: Parse.Attributes;
  itemParent: Parse.Attributes;
  itemChildren: Parse.Attributes[];
}

const ItemMove: React.VFC<ItemsMoveProps> = props => {
  const { itemMigration } = useGlobalConfig();
  const onlyCurrentItemType = itemMigration === 'currentItemType';
  const { t } = useI18n();
  // 时间轴显示文字
  const stepList = useMemo(
    () => [
      {
        text: t('pages.itemMove.step1'),
        index: STEP_INDEX.SELECT_WORKSPACE_AND_ITEMTYPE,
      },
      {
        text: t('pages.itemMove.step2'),
        index: STEP_INDEX.CHECK_FIELD_CHANGE,
      },
      {
        text: t('pages.itemMove.step3'),
        index: STEP_INDEX.CONFIRM_FIELD_CHANGE,
        nextText: t('pages.itemMove.moveText'),
      },
      {
        text: t('pages.itemMove.step4'),
        index: STEP_INDEX.FINISH,
        hideText: true,
      },
    ],
    [t],
  );
  const stepLayoutRef = useRef(null);
  const { tenant } = useToken();
  const { item, workspaceId, itemChildren, itemParent } = props;
  const [backLink, setBackLink] = useState<string>();
  const [step, setStep] = useState<StepType>(stepList[0]);
  const [currentScreenScheme, setCurrentScreenScheme] = useState('');
  const [currentWorkflow, setCurrentWorkflow] = useState<WorkflowDataProps>({ nodes: [], transitions: [] });
  const [currentItemType, setCurrentItemType] = useState<{ key: string; value: string; icon?: string }>({
    key: '',
    value: '',
  });
  const [securityLevelIsChange, setSecurityLevelIsChange] = useState<boolean>(false);
  const [currentWorkspace, setCurrentWorkspace] = useState<{ key: string; value: string }>({ key: '', value: '' });
  const [fieldNextFlag, setFieldNextFlag] = useState(false);
  const [changeFields, setChangeFields] = useState({});
  const [selectTransitionData, setSelectTransitionData] = useState<SelectTransitionType>(null);
  const [viewData, setViewData] = useState([]);
  const [requireFields, setRequireFields] = useState([]);
  const [workflowData, setWorkflowData] = useState(null);
  const [flowMessage, setFlowMessage] = useState<ResultType>(DEFAULT_FLOW_MESSAGE);
  const [result, setResult] = useState<{
    status: ResultStatusType;
    message?: string;
    data?: Parse.Attributes;
  }>({
    status: 'success',
    message: '',
    data: null,
  });

  useEffect(() => {
    // 获取事项工作流详情，用来比较工作流是否发生变化
    const getItemWorkflow = async () => {
      const workflow = await fetch.$get(`/workflows/item/${item.objectId}`);
      setWorkflowData(workflow);
    };
    getItemWorkflow();
    setBackLink(`/${tenant}/workspaces/${item.workspace?.key}`);
  }, [item.objectId, item.workspace?.key, tenant]);

  const workflowIsChange = useMemo(
    () => workflowData?.objectId !== currentWorkflow.objectId,
    [currentWorkflow.objectId, workflowData?.objectId],
  );

  const onWorkspaceOrItemTypeChange = useCallback(
    ({ workspaceData, itemTypeData, securityLevelIsChange: changeFlag }) => {
      log('change select data', workspaceData, itemTypeData);
      if (workspaceData) {
        setCurrentScreenScheme(workspaceData.itemTypeScreenScheme?.objectId);
        setCurrentWorkspace({ key: workspaceData.name, value: workspaceData.objectId });
      }

      itemTypeData && setCurrentItemType(itemTypeData);
      if (typeof changeFlag === 'boolean') {
        setSecurityLevelIsChange(changeFlag);
      }
    },
    [],
  );

  const fieldsChange = useCallback(fields => {
    setRequireFields(fields);
  }, []);

  const onFieldsChange = useCallback(({ transition, values }, nextFlag) => {
    log('change fields data', values, nextFlag);
    setChangeFields(values);
    setFieldNextFlag(nextFlag);
    setSelectTransitionData(transition);
  }, []);

  const onWorkflowChange = useCallback(data => {
    setCurrentWorkflow(data);
  }, []);

  const updateOperatorMessage = useCallback(message => {
    setFlowMessage(message);
  }, []);

  const contentComponent = useMemo(() => {
    switch (step.index) {
      case STEP_INDEX.SELECT_WORKSPACE_AND_ITEMTYPE:
        return (
          <SelectWorkspaceAndItemType
            itemId={item.objectId}
            workspaceId={workspaceId}
            itemChildren={itemChildren}
            itemParent={itemParent}
            title={t('pages.itemMove.step1Content')}
            onChange={onWorkspaceOrItemTypeChange}
            onWorkflowChange={onWorkflowChange}
            updateErrorMessage={updateOperatorMessage}
            currentItemTypeId={onlyCurrentItemType ? item.itemType.objectId : null}
          />
        );
      case STEP_INDEX.CHECK_FIELD_CHANGE:
        return (
          <ChangeOperator
            item={item}
            workspaceId={currentWorkspace.value}
            itemTypeId={currentItemType?.value}
            itemTypeScreenSchemeId={currentScreenScheme}
            itemWorkflow={workflowData}
            workflowData={currentWorkflow}
            workflowIsChange={workflowIsChange}
            title={t('pages.itemMove.step2Content')}
            onChange={onFieldsChange}
            updateRequireFields={fieldsChange}
            updateErrorMessage={updateOperatorMessage}
          />
        );
      case STEP_INDEX.CONFIRM_FIELD_CHANGE:
        return <ChangeView data={viewData} title={t('pages.itemMove.step3Content')} />;
      case STEP_INDEX.FINISH:
        return <FinishView itemId={item.objectId} result={result} jumpStep={stepLayoutRef?.current?.jumpStep} />;
      default:
        return null;
    }
  }, [
    step.index,
    item,
    workspaceId,
    onWorkspaceOrItemTypeChange,
    onWorkflowChange,
    currentWorkspace.value,
    currentItemType?.value,
    currentScreenScheme,
    currentWorkflow,
    workflowIsChange,
    workflowData,
    onFieldsChange,
    fieldsChange,
    updateOperatorMessage,
    viewData,
    result,
    itemChildren,
    itemParent,
    t,
  ]);

  const onStepChange = useCallback(
    newStep => {
      log('step change', newStep);
      setStep(newStep);
      if (newStep.index === STEP_INDEX.SELECT_WORKSPACE_AND_ITEMTYPE) {
        setCurrentItemType({ key: '', value: '' });
      }
      if (newStep.index === STEP_INDEX.CONFIRM_FIELD_CHANGE) {
        const viewData = [];
        if (workspaceId !== currentWorkspace.value) {
          viewData.push({
            before: { fieldType: Text, data: item.workspace.name },
            after: { fieldType: Text, data: currentWorkspace.key },
            type: 'workspace',
            name: t('global.workspace'),
          });
        }
        if (item.itemType.objectId !== currentItemType.value) {
          viewData.push({
            before: { fieldType: Text, data: item.itemType.name, icon: item.itemType.icon },
            after: {
              fieldType: Text,
              data: currentItemType.key,
              icon: currentItemType.icon,
              id: currentItemType.value,
            },
            type: 'itemType',
            name: t('global.itemType'),
          });
        }
        if (workflowIsChange) {
          viewData.push({
            before: { fieldType: Text, name: item.status.name, type: item.status.type },
            after: {
              fieldType: Text,
              name: selectTransitionData?.target?.name,
              type: selectTransitionData?.target?.type,
            },
            type: 'status',
            name: `${t('global.status')}(${selectTransitionData?.name})`,
          });
        }
        Object.keys(changeFields).forEach(key => {
          const fieldData = requireFields.find(field => field.key === key);
          viewData.push({
            name: fieldData?.name,
            before: { ...fieldData, data: item.values?.[key] },
            after: { ...fieldData, data: changeFields[key] },
          });
        });
        setViewData(viewData);
      }
    },
    [
      workspaceId,
      currentWorkspace.value,
      currentWorkspace.key,
      item.itemType.objectId,
      item.itemType.name,
      item.itemType.icon,
      item.workspace.name,
      item.status.name,
      item.status.type,
      item.values,
      currentItemType.value,
      currentItemType.key,
      currentItemType.icon,
      workflowIsChange,
      changeFields,
      selectTransitionData?.target?.name,
      selectTransitionData?.target?.type,
      selectTransitionData?.name,
      requireFields,
      t,
    ],
  );
  const canNext = useMemo((): boolean => {
    if (flowMessage.message) return false;
    switch (step.index) {
      case STEP_INDEX.SELECT_WORKSPACE_AND_ITEMTYPE:
        return (
          !!currentItemType.value &&
          !!currentScreenScheme &&
          (item.workspace.objectId !== currentWorkspace.value || item.itemType.objectId !== currentItemType.value)
        );
      case STEP_INDEX.CHECK_FIELD_CHANGE:
        return fieldNextFlag;
      case STEP_INDEX.CONFIRM_FIELD_CHANGE:
        return true;
      default:
        return false;
    }
  }, [
    currentItemType.value,
    currentScreenScheme,
    currentWorkspace.value,
    fieldNextFlag,
    item.itemType.objectId,
    item.workspace.objectId,
    step.index,
    flowMessage,
  ]);

  const beforeStep = useCallback(async () => {
    const submitMove = async () => {
      log('submit data');
      const itemDetail = {} as Item;
      if (workspaceId !== currentWorkspace.value) {
        itemDetail.workspace = Workspace.createWithoutData(currentWorkspace.value).toPointer();
      }
      if (item.itemType.objectId !== currentItemType.value) {
        itemDetail.itemType = ItemType.createWithoutData(currentItemType.value).toPointer();
      }
      if (workflowIsChange) {
        itemDetail.status = Status.createWithoutData(selectTransitionData?.target?.key).toPointer();
      }
      if (Object.keys(changeFields)?.length) {
        itemDetail.values = changeFields;
      }
      if (securityLevelIsChange) {
        itemDetail.securityLevel = null;
      }
      try {
        log('start move', item.objectId, itemDetail);
        const result = await updateItem(item.objectId, itemDetail);
        log('move after', item.objectId, result);
        setBackLink(null);
        return { status: 'success', data: result };
      } catch (error) {
        log('change error', error);
        let message = error.message || error.data?.error;
        if (error === 'timeout') {
          message = t('pages.itemMove.moveTimeOut');
        }
        return { status: 'warning', message, data: null };
      }
    };
    if (step.index === STEP_INDEX.SELECT_WORKSPACE_AND_ITEMTYPE) {
      setBackLink(`/${tenant}/workspaces/${item.workspace?.key}`);
    }
    if (step.index === STEP_INDEX.CONFIRM_FIELD_CHANGE) {
      return await submitMove();
    }
  }, [
    changeFields,
    currentItemType.value,
    currentWorkspace.value,
    item.itemType.objectId,
    item.objectId,
    item.workspace?.key,
    securityLevelIsChange,
    selectTransitionData?.target?.key,
    step.index,
    tenant,
    workflowIsChange,
    workspaceId,
    t,
  ]);

  const updateResult = useCallback(result => setResult(result), []);

  const itemHeader = useMemo(
    () => (
      <div className={cx('page-header')}>
        {t('pages.itemMove.currentItem')}
        <ItemTypeIcon name={item.itemType.name} icon={item.itemType.icon} className={cx('item-type-icon')} />
        {item.key}
        <span className={cx('page-header-title')}>{item.name}</span>
      </div>
    ),
    [item, t],
  );

  const error = useMemo(() => {
    if (
      step.index === STEP_INDEX.SELECT_WORKSPACE_AND_ITEMTYPE &&
      !canNext &&
      currentItemType.value &&
      currentWorkspace.value
    ) {
      return (
        <div className={cx('view-error')}>
          <Icons.Assist_Error />
          <span className={cx('text')}>{flowMessage.message || t('pages.itemMove.tip')}</span>
        </div>
      );
    }
    if (
      [STEP_INDEX.SELECT_WORKSPACE_AND_ITEMTYPE, STEP_INDEX.CHECK_FIELD_CHANGE].includes(step.index) &&
      !flowMessage.result &&
      flowMessage.message
    ) {
      return (
        <div className={cx('view-error')}>
          <Icons.Assist_Error />
          <span className={cx('text')}>{flowMessage.message}</span>
        </div>
      );
    }
    return null;
  }, [step.index, canNext, currentItemType.value, currentWorkspace.value, flowMessage, t]);

  return (
    <StepLayout
      ref={stepLayoutRef}
      backLink={backLink}
      title={t('pages.itemMove.title')}
      header={itemHeader}
      error={error}
      stepList={stepList}
      step={step}
      beforeStep={beforeStep}
      onStepChange={onStepChange}
      updateResult={updateResult}
      canNext={canNext}
    >
      {contentComponent}
    </StepLayout>
  );
};

export default ItemMove;
